/** Center-crop rectangle (in source-image pixels) that fills a square output. */
export function computeSquareCropRect(width: number, height: number): { x: number; y: number; size: number } {
    const size = Math.min(width, height);
    return {
        x: (width - size) / 2,
        y: (height - size) / 2,
        size,
    };
}

/**
 * Center-crop `file` to a square and downscale it to `size`×`size`, encoding
 * the result as a JPEG blob. Runs entirely client-side via an offscreen
 * canvas — there's no server-side image processing in this app.
 */
export async function resizeImageToSquareJpeg(file: File, size = 256, quality = 0.85): Promise<Blob> {
    const bitmap = await createImageBitmap(file);
    try {
        const crop = computeSquareCropRect(bitmap.width, bitmap.height);

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get 2D canvas context');

        ctx.drawImage(bitmap, crop.x, crop.y, crop.size, crop.size, 0, 0, size, size);

        return await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode resized image'))),
                'image/jpeg',
                quality,
            );
        });
    } finally {
        bitmap.close();
    }
}
